import React, { useEffect, useState } from 'react';
import {
   Button,
   GridRow,
   GridColumn,
   Input,
   Radio,
   Typography,
   Image,
   useGetMediaBreakpoint,
} from '@airtel-web/legos';
import { TEST_PATTERNS } from '../../../../common/constants/AppConstants';
import { NAME, MOBILE_NO } from './SwitchToAirtelConstants';
import BuyNewConnection from './view/buy-new-connection/BuyNewConnection';
import { SwitchToAirtelPropTypes } from './types/SwitchToAirtelPropTypes';
import { navigateTo } from '../../../../utils/utils';
import { fireAnalytics } from '../../service/HomePageAnalyticsService';
import { ANALYTICS_CONSTANTS } from '../../../../common/constants/AnalyticsConstants';
import colors from '@airtel-web/legos/lib/styles/variables/colors.module.scss';
import './SwitchToAirtel.scss';

const SwitchToAirtel = (props: SwitchToAirtelPropTypes) => {
   const [switchToAirtelForm, setSwitchToAirtelForm] = useState({
      name: '',
      mobileNo: '',
      lob: '',
      validations: {
         name: -1,
         mobileNo: -1,
         lob: -1,
      },
   });
   const mediaBreakpoint = useGetMediaBreakpoint();
   const isMobile = mediaBreakpoint === 'xs' || mediaBreakpoint === 'sm';

   const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (
         (e.target.id === MOBILE_NO &&
            e.target.value &&
            !TEST_PATTERNS.allNumeric.test(e.target.value)) ||
         (e.target.id === NAME &&
            e.target.value &&
            TEST_PATTERNS.alphabets.test(e.target.value))
      ) {
         return;
      }

      setSwitchToAirtelForm((oldState) => ({
         ...oldState,
         [e.target.id]: e.target.value,
         validations: {
            ...oldState.validations,
            [e.target.id]: checkFieldValidations(e.target.id, e.target.value),
         },
      }));

      if (e.target.id === NAME && !!checkFieldValidations(e.target.id, e.target.value)) {
         fireAnalytics({
            section: ANALYTICS_CONSTANTS.switchToAirtel,
            eventAction: ANALYTICS_CONSTANTS.click,
            eventLabel: ANALYTICS_CONSTANTS.enterName,
            eventValue: e.target.value?.toLowerCase(),
            customLabel: ANALYTICS_CONSTANTS.textbox,
            isInteractive: '1',
            horizontalPosition: '0',
            verticalPosition: '5',
         });
      } else if (
         e.target.id === MOBILE_NO &&
         !!checkFieldValidations(e.target.id, e.target.value)
      ) {
         fireAnalytics({
            section: ANALYTICS_CONSTANTS.switchToAirtel,
            eventAction: ANALYTICS_CONSTANTS.click,
            eventLabel: ANALYTICS_CONSTANTS.enterNumber,
            eventValue: e.target.value?.toLowerCase(),
            customLabel: ANALYTICS_CONSTANTS.textbox,
            isInteractive: '1',
            horizontalPosition: '0',
            verticalPosition: '5',
         });
      }
   };

   const onLobChange = (value: string, state: string) => {
      setSwitchToAirtelForm((oldState) => ({
         ...oldState,
         lob: state === 'on' ? value : '',
         validations: { ...oldState.validations, lob: 1 },
      }));
      fireAnalytics({
         section: ANALYTICS_CONSTANTS.switchToAirtel,
         eventAction: ANALYTICS_CONSTANTS.click,
         eventLabel: value?.toLowerCase(),
         customLabel: ANALYTICS_CONSTANTS.button,
         isInteractive: '1',
         horizontalPosition: '0',
         verticalPosition: '5',
      });
   };

   const checkFieldValidations = (id: string, value: string) => {
      if (id === MOBILE_NO && value.length === 10) {
         return TEST_PATTERNS.mobileNumber.test(value) ? 1 : 0;
      } else if (id === NAME) {
         return value.length === 0 || TEST_PATTERNS.alphabets.test(value) ? 0 : 1;
      } else if (id === 'lob') {
         return value === '' ? 0 : 1;
      }
   };

   const checkFormValidations = () => {
      return (
         switchToAirtelForm.validations.name === 1 &&
         switchToAirtelForm.validations.mobileNo === 1 &&
         switchToAirtelForm.validations.lob === 1
      );
   };

   const ctaEvents = {
      section: ANALYTICS_CONSTANTS.buyNewConnection,
      eventAction: ANALYTICS_CONSTANTS.impression,
      eventLabel: ANALYTICS_CONSTANTS.switchToAirtel,
      eventValue: ANALYTICS_CONSTANTS.selectAPlan,
      customLabel: ANALYTICS_CONSTANTS.button,
      isInteractive: '0',
      horizontalPosition: '6',
      verticalPosition: '5',
   };

   useEffect(() => {
      fireAnalytics(ctaEvents, 'buyNewConnection');
   }, []);

   const GetStartedClick = () => {
      fireAnalytics({
         ...ctaEvents,
         eventAction: ANALYTICS_CONSTANTS.click,
         customValue: '',
         isInteractive: '1',
      });
      navigateTo('https://www.airtel.in/myplan-infinity/?lf_open=499&requirement=p2p');
   };

   const handleGetStartedClick = () => {
      fireAnalytics({
         section: ANALYTICS_CONSTANTS.switchToAirtel,
         eventAction: ANALYTICS_CONSTANTS.click,
         eventLabel: 'get started',
         customLabel: ANALYTICS_CONSTANTS.button,
         isInteractive: '1',
         horizontalPosition: '0',
         verticalPosition: '5',
      });
      let url = '';
      if (switchToAirtelForm.lob === 'prepaid') {
         url = `https://www.airtel.in/prepaid-4g-sim/?icid=header&name=${switchToAirtelForm.name}&mobileNo=${switchToAirtelForm.mobileNo}`;
      } else if (switchToAirtelForm.lob === 'postpaid') {
         url = `https://www.airtel.in/myplan-infinity/?icid=header&name=${switchToAirtelForm.name}&mobileNo=${switchToAirtelForm.mobileNo}`;
      }
      navigateTo(url);
   };

   const renderSwitchToAirtelCard = () => {
      return (
         <GridRow className="switch-to-airtel-card" noMargin>
            <GridColumn
               noGutter
               className="switch-to-airtel-image-container"
               breakpoints={['col-xl-6', 'col-lg-5', 'col-md-6', 'col-sm-12']}
            >
               <Image
                  src={
                     isMobile
                        ? 'home-recast/images/switch-to-airtel-mobile.png'
                        : 'home-recast/images/switch-to-airtel-web.png'
                  }
                  width={isMobile ? '304' : '452'}
                  height={isMobile ? '124' : '493'}
                  alt="switch to airtel image"
                  className="switch-to-airtel-image"
                  useBaseUrl
                  draggable={false}
               />
            </GridColumn>
            <GridColumn
               breakpoints={['col-xl-6', 'col-lg-7', 'col-md-6', 'col-sm-12']}
               className="switch-to-airtel-form-container"
               noGutter
            >
               <div className="switch-to-airtel-form-container-inner">
                  <GridRow
                     className="switch-to-airtel-form"
                     direction="row-as-column"
                     justifyContent="center"
                     noMargin={isMobile}
                  >
                     <GridColumn noGutter className="switch-to-airtel-heading">
                        <Typography
                           type={isMobile ? 'body-para-xl-bold' : 'h4-bold'}
                           color={colors.black900}
                        >
                           Switch your number to airtel
                        </Typography>
                     </GridColumn>
                     <GridColumn noGutter className="switch-to-airtel-subheading">
                        <Typography
                           type={isMobile ? 'body-para-xxs' : 'body-para-md'}
                           color={colors.black900}
                        >
                           Share details &bull; Get SIM delivered &bull; Be with airtel in
                           24h
                        </Typography>
                     </GridColumn>
                     <GridColumn noGutter className="switch-to-airtel-input">
                        <Input
                           data-testid="switchToAirtel-name"
                           id="name"
                           label="Your name"
                           placeholder="e.g. John Doe"
                           value={switchToAirtelForm.name}
                           onChange={(e) => {
                              onInputChange(e);
                           }}
                           flexibleWidth
                           state={
                              switchToAirtelForm.validations.name === 0 ? 'error' : 'none'
                           }
                           hintText={
                              switchToAirtelForm.validations.name === 0
                                 ? 'Please enter your name'
                                 : ''
                           }
                           fixHintHeight={true}
                        />
                     </GridColumn>
                     <GridColumn noGutter className="switch-to-airtel-input">
                        <Input
                           data-testid="switchToAirtel-mobileNo"
                           id="mobileNo"
                           minLength={10}
                           maxLength={10}
                           label="Your mobile number"
                           placeholder="e.g. 999999999"
                           value={switchToAirtelForm.mobileNo}
                           onChange={(e) => {
                              onInputChange(e);
                           }}
                           flexibleWidth
                           state={
                              switchToAirtelForm.validations.mobileNo === 0
                                 ? 'error'
                                 : 'none'
                           }
                           hintText={
                              switchToAirtelForm.validations.mobileNo === 0
                                 ? 'Please enter a valid mobile number'
                                 : ''
                           }
                           prependText="+91"
                           showPrepend
                           fixHintHeight={true}
                        />
                     </GridColumn>
                     <GridColumn noGutter>
                        <div className="switch-to-airtel-radio-container">
                           <Radio
                              data-testid="switchToAirtel-prepaid"
                              id="prepaid-radio"
                              checked={switchToAirtelForm.lob === 'prepaid'}
                              label="Prepaid"
                              onChange={(e) => {
                                 onLobChange('prepaid', e.target.value);
                              }}
                           />
                           <Radio
                              data-testid="switchToAirtel-postpaid"
                              id="postpaid-radio"
                              checked={switchToAirtelForm.lob === 'postpaid'}
                              label="Postpaid"
                              onChange={(e) => {
                                 onLobChange('postpaid', e.target.value);
                              }}
                           />
                        </div>
                     </GridColumn>
                     <GridColumn noGutter className="switch-to-airtel-primary-cta">
                        <Button
                           data-testid="switchToAirtel-button"
                           buttonType="primary"
                           version="variable-width"
                           controlState={checkFormValidations() ? 'none' : 'disabled'}
                           onClick={handleGetStartedClick}
                        >
                           GET STARTED
                        </Button>
                     </GridColumn>
                  </GridRow>
               </div>
            </GridColumn>
         </GridRow>
      );
   };

   return (
      <div className="switch-to-airtel-root">
         <GridRow direction="row-as-column" className="switch-to-airtel-inner">
            <GridColumn alignSelf="center" layout="comfortable">
               <Typography
                  type={isMobile ? 'h6-bold' : 'h4-bold'}
                  color={colors.black900}
               >
                  Buy a new connection
               </Typography>
            </GridColumn>
            {props.switchToAirtelData && (
               <GridColumn layout="comfortable">
                  <BuyNewConnection linksCmsData={props.switchToAirtelData} />
               </GridColumn>
            )}
            {/* off for v1
            <GridColumn layout="comfortable">{renderSwitchToAirtelCard()}</GridColumn> */}
         </GridRow>
         <GridRow
            className="switch-your-number"
            justifyContent="center"
            id="buyNewConnection"
         >
            <Image
               src="https://assets.airtel.in/static-assets/home-recast/images/switch-to-airtel-web.png"
               className="switch-image"
               draggable={false}
            />
            <GridRow noMargin className="switch-to-airtel-column">
               <GridColumn noGutter breakpoints={['col-sm-12', 'col-md-8']}>
                  <GridRow
                     noMargin
                     direction="row-as-column"
                     className="switch-to-airtel-content"
                  >
                     <GridColumn noGutter className="switch-text">
                        <Typography
                           type={
                              isMobile
                                 ? 'body-single-line-xs-bold'
                                 : 'body-single-line-md-bold'
                           }
                           color={colors.black100}
                        >
                           Switch to airtel
                        </Typography>
                     </GridColumn>
                     <GridColumn noGutter className="get-sim-text">
                        <Typography
                           type={isMobile ? 'body-para-xxs' : 'body-single-line-xs'}
                           color={colors.black100}
                        >
                           Select a plan & order SIM &bull; Get SIM home-delivered
                        </Typography>
                     </GridColumn>
                  </GridRow>
               </GridColumn>
               <GridColumn noGutter breakpoints={['col-sm-12', 'col-md-4']}>
                  <GridRow noMargin justifyContent={isMobile ? 'unset' : 'end'}>
                     <Button
                        data-testid="switchToAirtel-button"
                        buttonType="alternate-light"
                        className="get-started-btn"
                        onClick={GetStartedClick}
                     >
                        GET STARTED
                     </Button>
                  </GridRow>
               </GridColumn>
            </GridRow>
         </GridRow>
      </div>
   );
};

export default SwitchToAirtel;
